import { apiErrorMessage } from '@agos/api-client'
import {
  getGetTranslationsQueryKey,
  getGetTranslationsStatsQueryKey,
  useDeleteTranslationsId,
  useGetTranslations,
  useGetTranslationsNamespaces,
  useGetTranslationsStats,
  usePatchTranslationsId,
} from '@agos/api-client/marketing'
import { T, formatCount, tt, useAuth } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  ConfirmDialog,
  EmptyState,
  FilterSelect,
  PageHeader,
  SearchInput,
  Spinner,
  StatCard,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Info, Languages, RefreshCw, Save, Trash2, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZES,
  STATS_POLL_MS,
  completenessLabel,
  coverageNote,
  editPayload,
  entryState,
  entryStateLabel,
  expectedRowsExplanation,
  filterLanguages,
  languageFlag,
  languageLabel,
  namespaceNote,
  namespacesOf,
  pageCount,
  pageLabel,
  pageOf,
  statsOf,
  type StatusFilter,
  type TranslationEntry,
} from './translation-memory'

/**
 * `/settings-translations` — the translation memory.
 *
 * **FB-159.** `GET /translations/stats` counts three languages and the memory holds five: `cs` (99 rows) and
 * `sk` (2) besides `hu`, `en` and `it`. `expectedRows` is `uniqueSources × 3`, presented as the denominator of
 * completeness — so the panel's percentage is a percentage of three languages, and 101 rows are in the memory
 * and in none of the figures. The languages counted are named, the target is explained as a target, and the
 * language filter offers the whole catalogue rather than the counted three.
 *
 * **FB-160.** Legacy builds the namespace filter from the fetched page — a hundred rows of 12 859, against 23
 * namespaces in the table — so its options change as you page and a namespace not on screen cannot be chosen.
 * **`GET /translations/namespaces` exists now** (`MIGRATION-STATUS`, 2026-08-02) and answers all 23, so the
 * filter is built from the memory. The page-derived list is the fallback while that request is in flight.
 *
 * Ported from legacy `pages/settings-translations.tsx` (689 lines).
 */

export function SettingsTranslationsPage() {
  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [lang, setLang] = useState('')
  const [namespace, setNamespace] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(0)
  const [editing, setEditing] = useState<TranslationEntry | null>(null)
  const [deleting, setDeleting] = useState<TranslationEntry | null>(null)

  const writable = mayWrite('translations', user?.systemRole)
  const readOnly = readOnlyReason('translations', user?.systemRole)

  // 350 ms after typing stops, as legacy does — a keystroke per request over 12 859 rows is a request per
  // keystroke the server did not need.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search)
      setPage(0)
    }, 350)
    return () => clearTimeout(timer)
  }, [search])

  const params = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
      ...(status === 'all' ? {} : { status }),
      ...(lang === '' ? {} : { lang }),
      ...(namespace === '' ? {} : { namespace }),
      ...(debounced.trim() === '' ? {} : { search: debounced.trim() }),
    }),
    [pageSize, page, status, lang, namespace, debounced],
  )

  const list = useGetTranslations(params)
  // The stats are a snapshot: refreshed on an interval and when the tab is focused, so a newly added product
  // shows as missing without anybody clicking.
  const statsQuery = useGetTranslationsStats({
    query: { refetchInterval: STATS_POLL_MS, refetchOnWindowFocus: true },
  })

  const { entries, total } = pageOf(list.data)
  const stats = statsOf(statsQuery.data)
  // **From the memory, not from the page.** `namespacesOf(entries)` reads whichever namespaces are on
  // the hundred rows currently fetched, so the options changed as you paged and a namespace not on
  // screen could not be chosen at all — FB-160. `GET /translations/namespaces` answers all 23. The
  // page-derived list stays as the fallback for the moment the request is in flight, so the filter is
  // never empty.
  const { data: allNamespaces } = useGetTranslationsNamespaces()
  const onThisPage = namespacesOf(entries)
  const namespaces = (allNamespaces ?? []).length > 0 ? (allNamespaces ?? []) : onThisPage
  const namespaceWarning = namespaceNote(entries, total)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetTranslationsQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetTranslationsStatsQueryKey() })
  }

  const save = usePatchTranslationsId({
    mutation: {
      onSuccess: () => {
        invalidate()
        setEditing(null)
        toast({ title: tt('Preklad uložený') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const remove = useDeleteTranslationsId({
    mutation: {
      onSuccess: () => {
        invalidate()
        setDeleting(null)
        toast({ title: tt('Preklad zmazaný') })
      },
      onError: (caught) =>
        toast({ variant: 'destructive', title: tt('Mazanie zlyhalo'), description: apiErrorMessage(
                                                                                     caught,
                                                                                     tt('Riadok zostal.'),
                                                                                   ) }),
    },
  })

  const pages = pageCount(total, pageSize)

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Preklady')}
        description={tt('Pamäť prekladov - čo je preložené a čo chýba')}
        icon={<Languages className="w-5 h-5" />}
        actions={
          <button
            type="button"
            data-refresh
            disabled={list.isFetching}
            onClick={invalidate}
            className={cn(BUTTON_SECONDARY, 'flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm')}
          >
            <RefreshCw className={cn('w-4 h-4', list.isFetching && 'animate-spin')} /> <T>Obnoviť</T>
          </button>
        }
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      {stats !== null && (
        <>
          {/* FB-159. What the figures cover, before the figures. */}
          <p className="text-xs text-amber-500 flex items-start gap-2" data-coverage>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {coverageNote(stats, total)}
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              label={tt('Zdrojové texty')}
              value={formatCount(stats.uniqueSources)}
              icon={Languages}
            />
            <StatCard
              label={tt('Cieľ riadkov')}
              value={formatCount(stats.expectedRows)}
              sub={expectedRowsExplanation(stats)}
              color="blue"
            />
            <StatCard label={tt('Chýba')} value={formatCount(stats.missing)} color="amber" />
            <StatCard
              label={tt('Hotové')}
              value={completenessLabel(stats)}
              sub={`${formatCount(stats.translated)} + ${formatCount(stats.untranslatable)}`}
              color="emerald"
            />
          </div>

          <div className="bg-card border border-border rounded-2xl overflow-hidden" data-per-lang>
            <div className="divide-y divide-border">
              {stats.perLang.map((row) => (
                <div key={row.lang} className="px-5 py-3 flex items-center gap-3" data-lang-row={row.lang}>
                  <span className="w-28 text-sm font-semibold">
                    {languageFlag(row.lang)} {languageLabel(row.lang)}
                  </span>
                  <span className="text-xs text-muted-foreground flex-1">
                    <T>preložené</T> {formatCount(row.translated)} · <T>ručne</T> {formatCount(row.manual)} ·{' '}
                    <T>neprekladá sa</T> {formatCount(row.untranslatable)}
                  </span>
                  <StatusBadge
                    label={`${tt('chýba')} ${formatCount(row.missing)}`}
                    color={row.missing === 0 ? 'emerald' : 'amber'}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px]">
          <SearchInput value={search} onChange={setSearch} placeholder={tt('Hľadať v textoch')} />
        </div>
        <FilterSelect
          label={tt('Jazyk')}
          value={lang}
          onChange={(value) => {
            setLang(value)
            setPage(0)
          }}
          options={[
            { value: '', label: tt('Všetky jazyky') },
            // FB-159: the whole catalogue, not the three the stats count.
            ...filterLanguages().map((entry) => ({
              value: entry.code,
              label: `${entry.flag} ${entry.label}`,
            })),
          ]}
        />
        <FilterSelect
          label={tt('Menný priestor')}
          value={namespace}
          onChange={(value) => {
            setNamespace(value)
            setPage(0)
          }}
          options={[
            { value: '', label: tt('Všetky') },
            ...namespaces.map((entry) => ({ value: entry, label: entry })),
          ]}
        />
        <FilterSelect
          label={tt('Stav')}
          value={status}
          onChange={(value) => {
            setStatus(value as StatusFilter)
            setPage(0)
          }}
          options={[
            { value: 'all', label: tt('Všetky') },
            { value: 'translated', label: entryStateLabel('translated') },
            { value: 'manual', label: entryStateLabel('manual') },
            { value: 'untranslatable', label: entryStateLabel('untranslatable') },
          ]}
        />
        <FilterSelect
          label={tt('Na stránku')}
          value={String(pageSize)}
          onChange={(value) => {
            setPageSize(Number(value))
            setPage(0)
          }}
          options={PAGE_SIZES.map((size) => ({ value: String(size), label: String(size) }))}
        />
      </div>

      {/* FB-160. The namespace options come from this page. */}
      {namespaceWarning !== '' && (
        <p className="text-[11px] text-amber-500 flex items-start gap-2" data-namespace-note>
          <Info className="w-3 h-3 shrink-0 mt-0.5" />
          {namespaceWarning}
        </p>
      )}

      {list.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<Languages className="w-10 h-10" />}
          title={tt('Žiadny preklad')}
          description={tt('V tomto výbere nie je nič.')}
        />
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center gap-3 text-xs text-muted-foreground">
            <span data-total>
              {formatCount(total)} <T>záznamov</T>
            </span>
            <span className="ml-auto" data-page-label>
              {pageLabel(page, total, pageSize)}
            </span>
            <button
              type="button"
              data-prev
              aria-label={tt('Predchádzajúca strana')}
              disabled={page === 0 || list.isFetching}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              className="px-2 py-1 rounded border border-border disabled:opacity-40"
            >
              ‹
            </button>
            <button
              type="button"
              data-next
              aria-label={tt('Nasledujúca strana')}
              disabled={page + 1 >= pages || list.isFetching}
              onClick={() => setPage((current) => current + 1)}
              className="px-2 py-1 rounded border border-border disabled:opacity-40"
            >
              ›
            </button>
          </div>
          <div className="divide-y divide-border">
            {entries.map((entry) => {
              const state = entryState(entry)
              return (
                <div
                  key={entry.id}
                  className="px-5 py-3 flex items-start gap-4"
                  data-entry={entry.id}
                  data-entry-state={state}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{entry.sourceText}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {languageFlag(entry.targetLang)} {entry.translation === '' ? '-' : entry.translation}
                    </p>
                    {entry.namespace !== null && (
                      <p className="text-[10px] font-mono text-muted-foreground/60 mt-0.5">
                        {entry.namespace}
                      </p>
                    )}
                  </div>
                  <StatusBadge
                    label={entryStateLabel(state)}
                    color={
                      state === 'missing'
                        ? 'amber'
                        : state === 'manual'
                          ? 'blue'
                          : state === 'untranslatable'
                            ? 'zinc'
                            : 'emerald'
                    }
                  />
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      data-edit={entry.id}
                      disabled={!writable}
                      onClick={() => setEditing(entry)}
                      className="px-2 py-1 rounded border border-border text-xs disabled:opacity-40"
                    >
                      <T>Upraviť</T>
                    </button>
                    <button
                      type="button"
                      data-delete={entry.id}
                      aria-label={tt('Zmazať')}
                      disabled={!writable}
                      onClick={() => setDeleting(entry)}
                      className="p-1.5 rounded text-muted-foreground hover:text-destructive disabled:opacity-40"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {editing !== null && (
        <EntryEditor
          entry={editing}
          saving={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(next) => save.mutate({ id: editing.id, data: { ...editPayload(next) } })}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        variant="danger"
        title={tt('Zmazať preklad?')}
        description={
          deleting === null
            ? ''
            : `${deleting.sourceText} → ${languageLabel(deleting.targetLang)}. ${tt('Pri ďalšom použití sa preloží znova, ak je automatický preklad zapnutý.')}`
        }
        confirmLabel={tt('Zmazať')}
        icon={<Trash2 className="w-5 h-5" />}
        loading={remove.isPending}
        onConfirm={() => {
          if (deleting !== null) remove.mutate({ id: deleting.id })
        }}
      />
    </div>
  )
}

function EntryEditor({
  entry,
  saving,
  onClose,
  onSave,
}: {
  entry: TranslationEntry
  saving: boolean
  onClose: () => void
  onSave: (next: {
    translation: string
    isManualOverride: boolean
    isUntranslatable: boolean
  }) => void
}) {
  const [translation, setTranslation] = useState(entry.translation)
  const [untranslatable, setUntranslatable] = useState(entry.isUntranslatable)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-editor>
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold">{entry.sourceText}</p>
            <p className="text-xs text-muted-foreground">
              {languageFlag(entry.targetLang)} {languageLabel(entry.targetLang)}
              {entry.context !== null && ` · ${entry.context}`}
            </p>
          </div>
          <button type="button" aria-label={tt('Zavrieť')} onClick={onClose} className="p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <textarea
          aria-label={tt('Preklad')}
          rows={4}
          className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
          value={translation}
          onChange={(event) => setTranslation(event.target.value)}
        />

        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            data-untranslatable
            checked={untranslatable}
            onChange={(event) => setUntranslatable(event.target.checked)}
          />
          <T>Neprekladať - text zostane v pôvodnom jazyku</T>
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-border text-sm">
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            data-save-entry
            disabled={saving}
            onClick={() =>
              // A hand-typed translation is a manual override by definition — legacy makes the operator
              // tick a box for something the act of typing already decided.
              onSave({ translation, isManualOverride: true, isUntranslatable: untranslatable })
            }
            className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {saving ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť</T>
          </button>
        </div>
      </div>
    </div>
  )
}
